import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Share2, Copy, CheckCircle, Link2, Users, BarChart3, TrendingUp, MessageCircle, Gift, Zap, AlertTriangle, Lock, MapPin, Target, HeartCrack, Smile } from 'lucide-react'
import useStore from '../store'

// 预设分享文案模板
const SHARE_TEMPLATES = [
  {
    id: 'default',
    icon: MessageCircle,
    title: '标准版',
    content: '硬条件测评拿了85分，以为稳了，结果系统提示跟我一样条件的"神仙"太多了，属于同质化内卷区，这年头好条件也不吃香了，你们来卷卷看。',
    tag: '推荐'
  },
  {
    id: 'data',
    icon: BarChart3,
    title: '数据版',
    content: '我参加央国企求职测评，得了72分，竞争比想象中激烈，今年这么难吗？你们也来试试能拿多少分。',
    tag: '热门'
  },
  {
    id: 'benefit',
    icon: Gift,
    title: '福利版',
    content: '我参加央国企求职测评，得了88分，看来留北京没啥希望了，你们也来试试。',
    tag: '免费'
  },
  {
    id: 'urgent',
    icon: Zap,
    title: '紧迫版',
    content: '央国企测评68分，系统直接建议我放弃总部管培生，去卷三级子公司的基层岗……虽然难听，但好像确实是这么回事，有没有人一起研究下怎么曲线救国？',
    tag: '限时'
  },
  {
    id: 'low',
    icon: TrendingUp,
    title: '低分版',
    content: "央企求职测评喜提53分，系统建议我'拓宽就业视野，不要死磕体制内'……翻译一下就是让我赶紧去私企打工是吧。纯纯的炮灰实锤了，你们快去测测，给自己找点心理平衡。",
    tag: '扎心'
  },
  {
    id: 'major',
    icon: AlertTriangle,
    title: '专业背刺版',
    content: '央国企测评74分，全靠学校撑着。系统说我专业被标了"红牌警告"，直接扣了一大截……难怪之前网申总挂，原来是被专业背刺了，你们快去查查。',
    tag: '破防'
  },
  {
    id: 'hidden',
    icon: Lock,
    title: '隐性门槛版',
    content: '拿了75分，结果带个括号备注：受年龄/性别隐性限制，部分核心岗被锁。终于知道为什么投简历石沉大海了，你们也去测测有没有被暗中针对。',
    tag: '揭秘'
  },
  {
    id: 'geo',
    icon: MapPin,
    title: '地缘锁死版',
    content: '系统直接判定我不回老家/生源地就是"纯陪跑"……大城市梦碎了，这测评真是不留情面，你们也去看看自己被地缘卡死没。',
    tag: '扎心'
  },
  {
    id: 'position',
    icon: Target,
    title: '精准定位版',
    content: '系统没劝退，只是淡淡留了一句"建议将预期调整为地方城投/非核心岗"……行吧，起码让我知道自己的底线在哪了，你们测测看自己什么段位。',
    tag: '清醒'
  },
  {
    id: 'soul',
    icon: HeartCrack,
    title: '直击灵魂版',
    content: '系统冷冰冰提示"与央企基础筛选模型匹配度极低，不建议盲目投递"……连做炮灰的资格都没有，太扎心了，你们来比比惨。',
    tag: '暴击'
  },
  {
    id: 'irony',
    icon: Smile,
    title: '高级反讽版',
    content: '系统委婉地建议我"重点关注市场化程度较高的混合所有制企业"……说人话就是别来蹭国企热度了，去私企卷吧。',
    tag: '反讽'
  }
]

const SharePage = () => {
  const [searchParams] = useSearchParams()
  const { user } = useStore()
  
  const [shareLink, setShareLink] = useState('')
  const [shareCode, setShareCode] = useState('')
  const [copied, setCopied] = useState(false)
  const [stats, setStats] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(0)
  const [showTemplates, setShowTemplates] = useState(false)

  // Get ref from URL if visiting via share link
  useEffect(() => {
    const ref = searchParams.get('ref')
    if (ref) {
      fetch('/api/shares/click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shareCode: ref,
          ip: 'client',
          userAgent: navigator.userAgent
        })
      }).catch(() => {})
      useStore.getState().setReferrerCode(ref)
    }
  }, [searchParams])

  const generateShareLink = async () => {
    setGenerating(true)
    try {
      const response = await fetch('/api/shares/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.id || 'USER' + Date.now(),
          userName: user?.name || '用户' + Date.now()
        })
      })
      
      const data = await response.json()
      if (data.status === 200) {
        setShareLink(data.data.shareLink)
        setShareCode(data.data.shareCode)
        fetchStats(data.data.shareCode)
      }
    } catch (error) {
      console.error('生成分享链接失败:', error)
    } finally {
      setGenerating(false)
    }
  }

  const fetchStats = async (code) => {
    if (!code) return
    try {
      const response = await fetch(`/api/shares/stats/${code}`)
      const data = await response.json()
      if (data.status === 200) {
        setStats(data.data)
      }
    } catch (error) {
      console.error('获取统计失败:', error)
    }
  }

  const copyLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const copyWithTemplate = (templateIndex) => {
    const template = SHARE_TEMPLATES[templateIndex]
    const fullText = `${template.content}\n\n${shareLink}\n\n#央国企求职 #应届生求职 #求职测评`
    navigator.clipboard.writeText(fullText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const getQRCodeUrl = () => {
    if (!shareLink) return ''
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareLink)}`
  }

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <Share2 className="text-primary-600" size={32} />
        </div>
        <h1 className="section-title">分享赚收益</h1>
        <p className="section-subtitle">分享专属链接，好友测评成交后获得奖励</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card p-6 md:p-8">
        {!shareLink ? (
          <div className="text-center">
            <div className="mb-6">
              <div className="flex justify-center space-x-8 mb-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Link2 className="text-blue-600" size={20} />
                  </div>
                  <p className="text-sm text-gray-600">生成链接</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Users className="text-green-600" size={20} />
                  </div>
                  <p className="text-sm text-gray-600">好友测评</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="text-yellow-600" size={20} />
                  </div>
                  <p className="text-sm text-gray-600">获得收益</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-medium text-gray-900 mb-2">分享规则</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>1. 点击生成专属分享链接</li>
                  <li>2. 选择分享文案或直接复制链接</li>
                  <li>3. 分享给微信好友、朋友圈或社群</li>
                  <li>4. 好友通过链接完成测评或购买套餐</li>
                  <li>5. 获得佣金奖励，实时到账</li>
                </ul>
              </div>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={generateShareLink}
              disabled={generating}
              className="w-full btn-primary text-lg py-4"
            >
              {generating ? '生成中...' : '生成我的专属链接'}
            </motion.button>
          </div>
        ) : (
          <div>
            {/* Link */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">我的专属链接</label>
              <div className="flex">
                <input type="text" value={shareLink} readOnly className="flex-1 input-field rounded-r-none" />
                <button
                  onClick={copyLink}
                  className={`px-4 py-2 rounded-r-lg font-medium transition-colors ${
                    copied ? 'bg-green-500 text-white' : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  {copied ? (
                    <span className="flex items-center"><CheckCircle size={16} className="mr-1" /> 已复制</span>
                  ) : (
                    <span className="flex items-center"><Copy size={16} className="mr-1" /> 复制</span>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">分享码: {shareCode}</p>
            </div>

            {/* QR Code */}
            <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                <img src={getQRCodeUrl()} alt="分享二维码" className="w-40 h-40" />
              </div>
              <div className="flex-1 w-full">
                <h4 className="font-medium text-gray-900 mb-3">选择分享文案</h4>
                <div className="space-y-2">
                  {SHARE_TEMPLATES.map((template, index) => (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(index)
                        copyWithTemplate(index)
                      }}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        selectedTemplate === index 
                          ? 'border-primary-500 bg-primary-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <template.icon size={16} className="mr-2 text-primary-600" />
                          <span className="text-sm font-medium">{template.title}</span>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
                          {template.tag}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">{template.content.substring(0, 30)}...</p>
                    </button>
                  ))}
                </div>
                
                <button 
                  onClick={() => copyWithTemplate(selectedTemplate)}
                  className="w-full mt-3 py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                >
                  复制选中文案+链接
                </button>
              </div>
            </div>

            {/* Preview */}
            <AnimatePresence>
              {showTemplates && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 p-4 bg-gray-50 rounded-lg"
                >
                  <p className="text-sm text-gray-600 mb-2">文案预览：</p>
                  <div className="text-sm text-gray-800 bg-white p-3 rounded border">
                    {SHARE_TEMPLATES[selectedTemplate].content}
                    <br /><br />
                    {shareLink}
                    <br /><br />
                    #央国企求职 #应届生求职 #求职测评
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            <button 
              onClick={() => setShowTemplates(!showTemplates)}
              className="text-sm text-primary-600 hover:text-primary-700 mb-6"
            >
              {showTemplates ? '隐藏预览' : '预览完整文案'}
            </button>

            {/* Stats */}
            {stats && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="border-t border-gray-200 pt-6">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                  <BarChart3 size={18} className="mr-2 text-primary-600" />
                  数据统计
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{stats.clickCount}</p>
                    <p className="text-xs text-gray-600">点击次数</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{stats.assessmentCount}</p>
                    <p className="text-xs text-gray-600">测评人数</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-yellow-600">{stats.orderCount}</p>
                    <p className="text-xs text-gray-600">成交订单</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">¥{stats.revenue?.toFixed(2)}</p>
                    <p className="text-xs text-gray-600">累计收益</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">转化率</span>
                    <span className="font-medium text-gray-900">{stats.conversionRate}</span>
                  </div>
                </div>
              </motion.div>
            )}

            <button onClick={() => { setShareLink(''); setStats(null); }} className="mt-6 w-full py-2 text-sm text-gray-500 hover:text-gray-700">
              重新生成链接
            </button>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default SharePage
